import React, { useMemo } from 'react'

import {
  GetServerSidePropsContext,
  InferGetServerSidePropsType,
  NextPage,
} from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useRouter } from 'next/router'

import { cx } from '@linaria/core'
import { withIronSessionSsr } from 'iron-session/next'
import InfiniteScroll from 'react-infinite-scroll-component'
import invariant from 'tiny-invariant'

import { useFoodSWR, useFoodSWRInfinite } from '~/api-client/food_api'
import { userGetProfile } from '~/api-client/food_api/user'
import { User } from '~/api-definition/definition'
import { Button, ButtonGroup } from '~/components/action/Button'
import { AppImage } from '~/components/data-display/AppImage'
import { UserFollowButton } from '~/components/food_app/user/UserFollowButton'
import { UserWarningStatusIconButton } from '~/components/food_app/user/UserWarningStatus'
import { ChevronLeftIcon } from '~/components/icons'
import { Container, ContainerInset } from '~/components/layout/Container'
import { Footer } from '~/components/layout/Footer'
import { Header } from '~/components/layout/Header'
import { config } from '~/config'
import { userProfileIcon } from '~/libs/user'
import { sessionOptions } from '~/session'

const Page: NextPage<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  props
) => {
  const router = useRouter()

  const followingUsers = useFoodSWRInfinite(
    '/user/following_users',
    {
      userId: props.userResponse.user.userId,
    },
    (pageIndex, _) => {
      return {
        count: config.pageSize.userFollowList,
        offset: pageIndex * config.pageSize.userFollowList,
      }
    }
  )
  const followings = useMemo(() => {
    return (
      followingUsers.data?.reduce((acc, resp) => {
        return [...acc, ...resp.users]
      }, [] as User[]) ?? []
    )
  }, [followingUsers.data])

  const followedUsers = useFoodSWR('/user/followed_users', {
    userId: props.userResponse.user.userId,
  })

  return (
    <>
      <Head>
        <title>友達リスト</title>
      </Head>
      <Header>
        <div className="w-5">
          <ChevronLeftIcon fill="#fff" onClick={() => router.back()} />
        </div>
        {props.userResponse.user.userName}の友達リスト
        <div className="w-5" />
      </Header>

      <Container>
        <ContainerInset>
          <div className="flex flex-col gap-5">
            <ButtonGroup className="w-full">
              <Link
                href={`/foodies/${props.userResponse.user.userName}/follower`}
              >
                <Button variant="functionWeak">
                  フォロワー({followedUsers.data?.paging.all})
                </Button>
              </Link>
              <Link
                href={`/foodies/${props.userResponse.user.userName}/follow`}
              >
                <Button variant="functionWeak" data-current={true}>
                  フォロー({followingUsers.data?.[0]?.paging.all})
                </Button>
              </Link>
            </ButtonGroup>
          </div>

          <InfiniteScroll
            className="flex flex-col mx-auto w-full gap-y-4"
            dataLength={followings?.length ?? 0}
            next={() => {
              followingUsers.setSize((size) => size + 1)
            }}
            hasMore={
              (followingUsers.data &&
                followingUsers.data[followingUsers.data.length - 1]?.users
                  .length !== 0) ??
              false
            }
            loader={<div className="loader" key={0} />}
          >
            {followings.map((user, i, users) => {
              return (
                <div
                  key={user.userId}
                  className={cx(
                    'py-5',
                    users.length - 1 !== i &&
                      'border-b border-dashed border-[#CCCCCC]'
                  )}
                >
                  <Link href={`/foodies/${user.userName}`}>
                    <div className="flex gap-4">
                      <div className="min-w-[54px]">
                        <AppImage
                          variant={{ type: 'profile', user: user }}
                          rounded="full"
                          width={50}
                          height={50}
                          className="rounded-full"
                          alt={`${user.displayName}さんのプロフィール画像`}
                          src={userProfileIcon(user)}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <div className="font-bold text-sm flex items-center gap-1">
                            {user.displayName}
                            {user.warningStatus != null && (
                              <UserWarningStatusIconButton
                                userWarningStatus={user.warningStatus}
                              />
                            )}
                          </div>
                        </div>
                        <div className="whitespace-pre-wrap break-words text-[13px] text-[#6C6C6C]">
                          @{user.userName}
                        </div>
                      </div>
                      <div className="flex items-center">
                        {user.userId !== props.myUserId ? (
                          <UserFollowButton
                            user={user}
                            onChangeFollowingStatus={async () => {
                              await followingUsers.mutate()
                            }}
                          />
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              )
            })}
          </InfiniteScroll>
        </ContainerInset>
      </Container>
      <Footer />
    </>
  )
}

export const getServerSideProps = withIronSessionSsr(
  async (context: GetServerSidePropsContext) => {
    invariant(typeof context.query.user_name === 'string')

    const userResponse = await userGetProfile({
      accessToken: context.req.session.user?.accessToken,
      searchParams: {
        user_name: context.query.user_name,
      },
    })

    const props = {
      myUserId: context.req.session.user?.userId,
      isMe:
        context.req.session.user?.userId != null &&
        context.req.session.user?.userId === userResponse.user.userId,
      userResponse: userResponse,
    }

    return { props }
  },
  sessionOptions
)

export default Page
